import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faCode, faSpinner, faHeart, faStar } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import api from '../utils/api';
import { toast } from 'react-hot-toast';

const Projects = ({ darkMode }) => {
  const [visibleProjects, setVisibleProjects] = useState(6);
  const [isExpanded, setIsExpanded] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedProjects, setLikedProjects] = useState(new Set());

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/projects');
      
      if (response.data.success) {
        setProjects(response.data.projects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (e, websiteLink) => {
    e.stopPropagation();
    window.open(websiteLink, "_blank", "noopener,noreferrer");
  };

  const handleLikeProject = async (e, projectId) => {
    e.stopPropagation();
    
    if (likedProjects.has(projectId)) {
      toast.info('You have already liked this project!');
      return;
    }

    try {
      const response = await api.post(`/projects/${projectId}/like`);
      
      if (response.data.success) {
        setLikedProjects(prev => new Set([...prev, projectId]));
        // Update the project's like count in the local state
        setProjects(prev => prev.map(project => 
          project._id === projectId 
            ? { ...project, likes: response.data.likes }
            : project
        ));
        toast.success('Project liked!');
      }
    } catch (error) {
      console.error('Error liking project:', error);
      toast.error('Failed to like project');
    }
  };

  const handleShowMore = () => {
    if (isExpanded) {
      setVisibleProjects(6);
      setIsExpanded(false);
    } else {
      setVisibleProjects(projects.length);
      setIsExpanded(true);
    }
  };

  return (
    <section className={`pb-2 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} id="projects">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="relative ">
            <h1 className={`text-center w-full lg:w-full text-3xl font-extrabold py-3 
                    ${darkMode ?
                "bg-gradient-to-r from-gray-200 to-gray-500 text-transparent bg-clip-text"
                : "bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text"
              }
                    before:content-[''] before:absolute before:left-1/2 before:-translate-x-1/2 
                    before:bottom-0.5 before:w-24 before:h-1 
                    ${darkMode ? "before:bg-gray-400" : "before:bg-blue-600"}
                    before:rounded-full`}
            >
               My Projects
            </h1>
            <div className={`absolute -z-10 top-0 left-1/2 -translate-x-1/2 w-48 h-48 
                    ${darkMode ? "bg-gray-700" : "bg-blue-200"} 
                    rounded-full blur-[80px] opacity-50`}>
            </div>
          </div>
          <p className={`text-sm max-w-3xl mx-auto leading-relaxed ${
            darkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Explore my portfolio of web applications, mobile apps, and innovative solutions. 
            Each project represents a unique challenge solved with modern technologies.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl mr-4" />
            <span className="text-xl">Loading projects...</span>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-2xl font-medium mb-2">No projects available</h3>
            <p className="text-gray-500">Check back later for new projects!</p>
          </div>
        ) : (
          <>
            <div className="grid lg:w-[85%] w-full mx-auto grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {projects.slice(0, visibleProjects).map((project, index) => (
                <motion.div
                  key={project._id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`group relative overflow-hidden rounded-2xl shadow-xl transform hover:scale-105 transition-all duration-300 ${
                    darkMode 
                      ? 'bg-gradient-to-br from-gray-800 to-gray-900 hover:shadow-2xl hover:shadow-blue-500/20' 
                      : 'bg-gradient-to-b from-blue-600/15  to-white hover:shadow-2xl hover:shadow-blue-500/20'
                  }`}
                >
                  <div className="relative overflow-hidden p-4 rounded-t-2xl">
                    <img
                      src={project.imageUrl}
                      alt={project.title}
                      className="w-full h-44 object-cover transition-transform duration-300 rounded-lg shadow-md shadow-blue-900/20 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x200?text=No+Image';
                      }}
                    />
                    {project.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                          <FontAwesomeIcon icon={faStar} />
                          Featured
                        </span>
                      </div>
                    )}
                  </div>
                    <div className='px-4 pb-4'>
                  <div className={`border-2 p-3 rounded-md
                    ${darkMode ? 'border-gray-700/15' : 'border-gray-400/10'}`}>
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="text-xl font-bold group-hover:text-blue-500 transition-colors duration-200">
                        {project.title}
                      </h3>
                      <div className={`flex items-center justify-between text-sm rounded-sm  mb-4 ${
                        darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-800'
                      }`}>
                      <span className="px-2 py-1 rounded text-sm font-medium">
                        {project.type}
                      </span>
                    </div>
                      
                    </div>
                    
                    <p className={`mb-4 capitalize leading-relaxed ${
                      darkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            darkMode
                              ? 'bg-blue-900/50 text-blue-300 border border-blue-700'
                              : 'bg-blue-100 text-blue-800 border border-blue-200'
                          }`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => handleViewClick(e, project.websiteLink)}
                        className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                          darkMode
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-lg hover:shadow-blue-500/25'
                            : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg hover:shadow-blue-500/25'
                        }`}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                        <span>View Live</span>
                      </button>
                      
                      <button
                        onClick={(e) => handleLikeProject(e, project._id)}
                        disabled={likedProjects.has(project._id)}
                        className={`py-3 px-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center ${
                          likedProjects.has(project._id)
                            ? 'bg-red-500 text-white cursor-not-allowed'
                            : darkMode
                              ? 'bg-gray-700 hover:bg-red-600 text-gray-300 hover:text-white border border-gray-600'
                              : 'bg-gray-100 hover:bg-red-500 text-gray-700 hover:text-white border border-gray-300'
                        }`}
                      >
                        <FontAwesomeIcon icon={faHeart} />
                      </button>
                      
                      <button
                        className={`py-3 px-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center ${
                          darkMode
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white border border-gray-600'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 border border-gray-300'
                        }`}
                      >
                        <FontAwesomeIcon icon={faCode} />
                      </button>
                    </div>
                  </div>
                    </div>
                </motion.div>
              ))}
            </div>

            {projects.length > visibleProjects && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
              >
                <button
                  onClick={handleShowMore}
                  className={`px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 ${
                    darkMode
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-lg hover:shadow-purple-500/25'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-purple-500/25'
                  }`}
                >
                  {isExpanded ? 'Show Less' : `Show More (${projects.length - visibleProjects} remaining)`}
                </button>
              </motion.div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

Projects.propTypes = {
  darkMode: PropTypes.bool.isRequired,
};

export default Projects;
